import { BindFancybox } from 'smg/fancybox';
import { SetBootstrapVersion } from 'smg/bootstrap';
import { GetPageRequestID } from 'smg/customer-api/pagerequests';
import * as session from 'smg/customer-api/session';
import * as errors from 'smg/errors';
import * as html from 'smg/html';
import { IconMarkup, SetIconLibrary } from 'smg/icons';
import * as meta from 'smg/meta';
import * as modals from 'smg/modal';
import * as paypal from 'smg/customer-api/paypal';
import QueryString from 'smg/querystring';
import * as carts from './carts';
import 'smg/growthbook';

import { InitToasts } from './toast';

declare interface myWindow extends Window {
    redditPixel?: string;
    metaPixel?: string;
}
declare const window: myWindow;

SetIconLibrary('materialSymbolsSharp');
SetBootstrapVersion(5);
InitToasts();

BindFancybox();

modals.SetDefaults({
    cancelText: 'Cancel',
    cancelButtonClass: 'btn btn-outline-secondary',
});

const errorIcon = IconMarkup({ fa4: 'fa-exclamation-circle', fa5: 'fa-exclamation-circle', ms: 'error', className: 'icon-5x text-danger', attrs: 'aria-hidden="true"' });
errors.Defaults.showErrorFunc = msg => new modals.default({
    body: html.FromString(`<div>
            <div class="text-center mb-5">${errorIcon}</div>
            <div class="fs-4 text-center">${html.Escape(msg)}</div>
        </div>`),
    cancelText: 'Close',
});

function initSession() {
    const navLogin = document.querySelectorAll('.nav-login');
    if (session.GetLoginToken()) {
        session.Session().then(sess => {
            const navName = document.querySelectorAll('.nav-name');
            if (navName.length) {
                navName.forEach(el => {
                    el.textContent = sess.user.fullName() || sess.user.company || 'My Account';
                });
            }

            const navSession = document.querySelector<HTMLElement>('#nav-session-item');
            if (navSession) {
                navSession.classList.remove('d-none');
            }

            document.querySelectorAll('.logout').forEach(e => e.addEventListener('click', e => {
                e.preventDefault();

                session.DeleteSession();
                window.location.reload();
            }));

            navLogin.forEach(el => el.classList.remove('d-none'));
        }).catch(err => {
            navLogin.forEach(el => el.classList.remove('d-none'));
            throw err;
        });
    } else {
        const notLoggedIn = document.querySelectorAll('.notLoggedIn');
        notLoggedIn.forEach(el => el.classList.remove('d-none'));
    }
}

function initCart() {
    if (carts.GetCartID()) {
        void carts.GetCartCount().then(count => {
            if (!count) {
                return;
            }

            carts.IncrementCartCount(count);
        });
    }
}

async function main() {
    await session.ConsumeLoginTokenFromURL();
    // Page tracking starts after the exchange so the page request belongs to the
    // account the link signed in, not whoever was signed in before.
    void GetPageRequestID();

    if (QueryString().get('paypal') === 'success') {
        const successAlert = document.querySelector('#paypal-success') as HTMLElement;
        successAlert.classList.remove('d-none');
        successAlert.addEventListener('closed.bs.alert', () => {
            successAlert.classList.add('d-none');
            QueryString().delete('paypal');
        });
    }

    if (QueryString().get('paypal') === 'error') {
        const successAlert = document.querySelector('#paypal-error') as HTMLElement;
        successAlert.classList.remove('d-none');
        successAlert.addEventListener('closed.bs.alert', () => {
            successAlert.classList.add('d-none');
            QueryString().delete('paypal');
        });
    }

    const paypalRetryURL = paypal.DeclinedRetryURL();
    if (paypalRetryURL) {
        const declinedAlert = document.querySelector('#paypal-declined') as HTMLElement;
        (declinedAlert.querySelector('#paypal-declined-retry') as HTMLAnchorElement).href = paypalRetryURL;
        declinedAlert.classList.remove('d-none');
        declinedAlert.addEventListener('closed.bs.alert', () => {
            declinedAlert.classList.add('d-none');
            QueryString().delete('paypal');
        });
    }

    initSession();
    initCart();

    // Slider galleries and the guide-download form come from shortcodes that
    // can appear on any page, so their triggers stay global — but each pulls
    // heavy deps (masonry, the models barrel), so lazy-load the implementations
    // only when their markup is actually on the page.
    if (document.querySelector('.slider-gallery')) {
        void import('./slider-galleries').
            then(sliderGalleries => sliderGalleries.default()).
            catch(err => console.error(`failed to load slider galleries: ${String(err)}`));
    }

    if (document.querySelector('.design-template-form')) {
        void import('./download-template').
            catch(err => console.error(`failed to load download template form: ${String(err)}`));
    }

    // Initialize Reddit pixel tracking
    if (window.rdt) {
        const rdt = window.rdt;
        if (session.GetLoginToken()) {
            void session.Session().then(sess => {
                rdt('init', window.redditPixel, {
                    'email': sess.user.emailAddress.email,
                    'phoneNumber': sess.user.phoneNumber.phone,
                    'externalId': sess.user.id,
                });
            }).catch(() => {
                // If session fails, initialize without user data
                rdt('init', window.redditPixel);
            }).finally(() => {
                rdt('track', 'PageVisit');
            });
        } else {
            rdt('init', window.redditPixel);
            rdt('track', 'PageVisit');
        }
    }

    // Initialize Meta (Facebook) pixel tracking
    if (window.metaPixel) {
        const pixelId = window.metaPixel;
        void session.Session().then(sess => {
            meta.InitializeMeta(pixelId, sess ? {
                email: sess.user.emailAddress.email,
                phone: sess.user.phoneNumber.phone,
                userId: sess.user.id,
            } : undefined);
        }).catch(() => {
            meta.InitializeMeta(pixelId);
        }).finally(() => {
            meta.TrackPageView();
        });
    }
}

void main();
